/**
 * useAnime — default React hook for one-shot or imperative animations.
 *
 * Returns a ref you attach to a DOM node plus a `play()` function that
 * runs the configured animation. Cleans up on unmount and respects
 * prefers-reduced-motion by skipping animation and applying the
 * end-state styles directly.
 *
 * Example:
 *   const { ref, play } = useAnime<HTMLDivElement>({
 *     opacity: [0, 1],
 *     translateY: [16, 0],
 *     duration: DURATION.base,
 *     ease: EASE.soft,
 *   });
 *   useEffect(() => { play(); }, [play]);
 *   return <div ref={ref}>...</div>;
 */

import { useCallback, useEffect, useRef } from "react";
import { animate, type AnimationParams, type JSAnimation } from "animejs";
import { prefersReducedMotion } from "./prefersReducedMotion";

export interface UseAnimeOptions extends AnimationParams {
  /** Skip animation and snap to the final state (default: respects OS setting). */
  reducedMotion?: boolean;
  /** Auto-play once on mount. Default: false. */
  autoPlay?: boolean;
}

export interface UseAnimeResult<T extends HTMLElement> {
  ref: React.RefObject<T>;
  /** Start (or restart) the animation. Optional override merges into the base config. */
  play: (override?: AnimationParams) => JSAnimation | null;
  /** Pause the currently-running animation if any. */
  pause: () => void;
}

/**
 * Apply the "final frame" of an animation config without playing it.
 * Used when prefers-reduced-motion is on. Reads end-of-tween values
 * from common transform / opacity keys; anything more exotic should be
 * applied by the caller.
 */
function snapToFinal(el: HTMLElement, params: AnimationParams): void {
  const pickEnd = (v: unknown): string | number | null => {
    if (Array.isArray(v) && v.length > 0) {
      const last = v[v.length - 1];
      if (typeof last === "number" || typeof last === "string") return last;
    }
    if (typeof v === "number" || typeof v === "string") return v;
    return null;
  };

  const p = params as Record<string, unknown>;

  const opacity = pickEnd(p.opacity);
  if (opacity !== null) el.style.opacity = String(opacity);

  // Box-model dimensions (progress bars, dividers)
  const width = pickEnd(p.width);
  if (width !== null) {
    el.style.width = typeof width === "number" ? `${width}px` : width;
  }
  const height = pickEnd(p.height);
  if (height !== null) {
    el.style.height = typeof height === "number" ? `${height}px` : height;
  }

  // SVG path-draw shorthand
  const sdo = pickEnd(p.strokeDashoffset);
  if (sdo !== null) {
    el.style.strokeDashoffset = typeof sdo === "number" ? String(sdo) : sdo;
  }

  // Transform components — combined into a single transform string
  const tx = pickEnd(p.translateX);
  const ty = pickEnd(p.translateY);
  const sc = pickEnd(p.scale);
  const sx = pickEnd(p.scaleX);
  const sy = pickEnd(p.scaleY);
  const rot = pickEnd(p.rotate);

  if (tx !== null || ty !== null || sc !== null || sx !== null || sy !== null || rot !== null) {
    const parts: string[] = [];
    if (tx !== null) parts.push(`translateX(${typeof tx === "number" ? `${tx}px` : tx})`);
    if (ty !== null) parts.push(`translateY(${typeof ty === "number" ? `${ty}px` : ty})`);
    if (sc !== null) parts.push(`scale(${sc})`);
    if (sx !== null) parts.push(`scaleX(${sx})`);
    if (sy !== null) parts.push(`scaleY(${sy})`);
    if (rot !== null) parts.push(`rotate(${typeof rot === "number" ? `${rot}deg` : rot})`);
    el.style.transform = parts.join(" ");
  }
}

export function useAnime<T extends HTMLElement = HTMLElement>(
  config: UseAnimeOptions,
): UseAnimeResult<T> {
  const ref = useRef<T>(null);
  const animationRef = useRef<JSAnimation | null>(null);
  // Hold the latest config in a ref so play() doesn't churn its identity
  // every render. Callers usually pass an inline object literal.
  const configRef = useRef<UseAnimeOptions>(config);
  configRef.current = config;

  const play = useCallback((override?: AnimationParams): JSAnimation | null => {
    const el = ref.current;
    if (!el) return null;

    const { reducedMotion, autoPlay: _autoPlay, ...params } = configRef.current;
    void _autoPlay;
    const merged: AnimationParams = { ...params, ...(override ?? {}) };

    const skip = reducedMotion ?? prefersReducedMotion();
    if (skip) {
      snapToFinal(el, merged);
      return null;
    }

    // Pause any in-flight animation so we don't double-tween.
    animationRef.current?.pause();
    const next = animate(el, merged);
    animationRef.current = next;
    return next;
  }, []);

  const pause = useCallback(() => {
    animationRef.current?.pause();
  }, []);

  // Optional auto-play on mount.
  useEffect(() => {
    if (configRef.current.autoPlay) {
      play();
    }
    return () => {
      animationRef.current?.pause();
      animationRef.current = null;
    };
    // play is stable; we intentionally only run this on mount/unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, play, pause };
}
